/**
 * React Hook for RTDB-cached odds with real-time updates
 */

import { useState, useEffect, useCallback } from 'react';
import rtdbFootballService from '../rtdbFootballService';
import rtdbCache from '../rtdbCache';
import { CACHE_PATHS, getCacheKey, logCache } from '../rtdbConfig';

/**
 * Hook for fetching fixture odds with RTDB caching
 * @param {Object} params - Query parameters
 * @param {number} params.fixtureId - Fixture ID
 * @param {boolean} params.enabled - Whether to fetch data
 * @param {boolean} params.realtime - Enable real-time updates for live odds
 * @returns {Object} Odds data, loading state, error, and refetch function
 */
export const useRTDBOdds = ({ fixtureId, enabled = true, realtime = true }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [cacheStatus, setCacheStatus] = useState('idle');

  const fetchOdds = useCallback(async () => {
    if (!enabled || !fixtureId) return;

    try {
      setLoading(true);
      setError(null);
      setCacheStatus('fetching');
      
      const response = await rtdbFootballService.getComprehensiveFixtureOdds(fixtureId);
      setData(response);
      setCacheStatus('success');
    } catch (err) {
      setError(err.message || 'Failed to load odds');
      setCacheStatus('error');
      console.error('Failed to load odds:', err);
    } finally {
      setLoading(false);
    }
  }, [fixtureId, enabled]);

  // Set up real-time subscription for live odds
  useEffect(() => {
    if (!enabled || !fixtureId || !realtime) {
      if (enabled && fixtureId) {
        fetchOdds();
      }
      return;
    }

    let unsubscribeLive = () => {};
    let unsubscribePrematch = () => {};
    
    // Subscribe to live odds updates
    const liveOddsPath = `${CACHE_PATHS.ODDS_LIVE}/${getCacheKey('fixture', fixtureId)}`;
    const prematchOddsPath = `${CACHE_PATHS.ODDS_PREMATCH}/${getCacheKey('fixture', fixtureId)}`;

    // Subscribe to live odds
    unsubscribeLive = rtdbCache.subscribe(liveOddsPath, (cachedData) => {
      if (cachedData) {
        logCache('info', 'Live odds update received', { fixtureId });
        setData(prev => ({
          ...prev,
          live: cachedData.response || [],
          lastUpdated: new Date().toISOString()
        }));
        setCacheStatus('realtime-update');
      }
    });

    // Subscribe to pre-match odds
    unsubscribePrematch = rtdbCache.subscribe(prematchOddsPath, (cachedData) => {
      if (cachedData) {
        logCache('info', 'Pre-match odds update received', { fixtureId });
        setData(prev => ({
          ...prev,
          preMatch: cachedData.response || [],
          lastUpdated: new Date().toISOString()
        }));
        setCacheStatus('realtime-update');
      }
    });

    // Initial fetch
    fetchOdds();

    // For live odds, refresh more frequently
    const interval = setInterval(() => {
      if (data?.live?.length > 0) {
        fetchOdds();
      }
    }, 30000); // Every 30 seconds for live odds

    return () => {
      unsubscribeLive();
      unsubscribePrematch();
      clearInterval(interval);
    };
  }, [fixtureId, enabled, realtime]); // Don't include fetchOdds to avoid loops

  return { 
    data, 
    loading, 
    error, 
    refetch: fetchOdds,
    cacheStatus,
    isConnected: rtdbCache.isConnected()
  };
};

/**
 * Hook specifically for live odds with higher refresh rate
 */
export const useRTDBLiveOdds = ({ fixtureId, enabled = true }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled || !fixtureId) return;

    let unsubscribe = () => {};
    const cachePath = `${CACHE_PATHS.ODDS_LIVE}/${getCacheKey('fixture', fixtureId)}`;

    const fetchLiveOdds = async () => {
      setLoading(true);
      try {
        const response = await rtdbFootballService.getLiveOdds(fixtureId);
        setData(response);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    // Subscribe to real-time updates
    unsubscribe = rtdbCache.subscribe(cachePath, (cachedData) => {
      if (cachedData) {
        setData(cachedData);
      } else {
        fetchLiveOdds();
      }
    });

    // Initial fetch
    fetchLiveOdds();

    // Refresh every 15 seconds for live odds
    const interval = setInterval(fetchLiveOdds, 15000);

    return () => {
      unsubscribe();
      clearInterval(interval);
    };
  }, [fixtureId, enabled]);

  return { data, loading, error };
};